import { ReactNode, useEffect, lazy, Suspense, useState } from "react";
import { useLocation } from "react-router-dom";
import Navigation from "./Navigation";
import Footer from "./Footer";
import { trackPageView, getUTMParams } from "@/lib/tracking";

// Lazy load non-critical widgets for better initial load performance
const ExitIntentPopup = lazy(() => import("./ExitIntentPopup"));
const ChatbotWidget = lazy(() => import("./ChatbotWidget"));
const CookieConsent = lazy(() => import("./CookieConsent"));

interface LayoutProps {
  children: ReactNode;
}

const Layout = ({ children }: LayoutProps) => {
  const { pathname } = useLocation();
  const [pagesViewed, setPagesViewed] = useState(0);

  // Scroll to top on route change, track page views, capture UTM params
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setPagesViewed(prev => prev + 1);
    trackPageView(pathname);
    getUTMParams();
  }, [pathname]);

  // Only show exit intent after viewing 3+ pages (reduces popup fatigue)
  const showExitIntent = pagesViewed >= 3;

  return (
    <div className="min-h-screen flex flex-col scroll-smooth">
      <Navigation />
      <main className="flex-1">{children}</main>
      <Footer />
      
      {/* Reduced Lead Generation Widgets - Only 2 touchpoints per marketing critique */}
      <Suspense fallback={null}>
        {/* Exit Intent: Only for users who viewed 3+ pages */}
        {showExitIntent && <ExitIntentPopup />}
        
        {/* Smart Chatbot: Primary engagement tool */}
        <ChatbotWidget />
        
        {/* Cookie Consent: Legal requirement */}
        <CookieConsent />
      </Suspense>
      
      {/* Removed per marketing critique to reduce popup fatigue:
          - StickyBottomBar (redundant with nav CTA)
          - SlideContactForm (use chatbot instead)
          - Generic newsletter signup (build list through quiz completions)
      */}
    </div>
  );
};

export default Layout;
